/**
 * VerifyHash — public receipt verification page.
 * Route: /verify/:hash
 * Anyone can visit this URL to see the chain-of-custody rows attached to a trip.
 * No PHI, no fare — just the hash, the ceremony timeline, and provider stamps.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { DUR, EASE, STAGGER } from "../lib/motion";
import { BreathingDot, ChainOfCustodyStitcher } from "../components/live/LiveWorld";

export default function VerifyHash() {
  const { hash } = useParams();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Simulated "fetch verify" — real implementation would hit an anonymous endpoint.
    const t = setTimeout(() => setReady(true), 900);
    return () => clearTimeout(t);
  }, [hash]);

  const cocRows = [
    { label: "Identity verified · driver + rider", stamp: "sealed", provider: "AWS" },
    { label: "Credentials current",                stamp: "sealed", provider: "MedexOva" },
    { label: "Rules loaded",                       stamp: "sealed", provider: "MedexOVA" },
    { label: "Pickup ceremony",                    stamp: "sealed", provider: "GPS" },
    { label: "POD ceremony",                       stamp: "sealed", provider: "POD" },
    { label: "Hash locked",                        stamp: hash?.slice(0, 8) + "…", provider: "SHA-256" },
  ];

  const stagger = {
    hidden: { opacity: 0 },
    show:   { opacity: 1, transition: { staggerChildren: STAGGER.dramatic } },
  };
  const item = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: DUR.reveal, ease: EASE.emerge } } };

  return (
    <div data-testid="verify-hash-root"
      style={{ minHeight: "100vh", background: "radial-gradient(circle at top,#111C2E 0%,#0B1220 60%)", color: "#E2E8F0", padding: "48px 16px" }}>
      <div style={{ maxWidth: 520, margin: "0 auto" }}>
        <motion.div variants={stagger} initial="hidden" animate="show" style={{ display: "grid", gap: 20 }}>
          <motion.header variants={item}>
            <Link to="/" style={{ fontSize: 11, color: "#94A3B8", letterSpacing: "0.14em", textTransform: "uppercase", textDecoration: "none" }}>
              ← MedexOVA
            </Link>
            <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 8 }}>
              <BreathingDot color="#10B981"/>
              <span style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "#10B981", fontWeight: 800 }}>Public verification</span>
            </div>
            <div style={{ fontSize: 30, fontWeight: 900, marginTop: 6, letterSpacing: "-0.02em" }}>Trip is verified.</div>
            <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 8, lineHeight: 1.55 }}>
              This hash matches an MedexOVA trip receipt. Every row below was independently stamped and cannot be altered after the fact.
            </div>
          </motion.header>

          <motion.section variants={item} data-testid="verify-hash-hash"
            style={{ padding: 16, borderRadius: 12, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", fontFamily: "ui-monospace,Menlo,monospace", fontSize: 11, color: "#94A3B8", wordBreak: "break-all" }}>
            {hash}
          </motion.section>

          <motion.section variants={item} data-testid="verify-hash-coc"
            style={{ padding: 18, borderRadius: 16, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#94A3B8", fontWeight: 800, marginBottom: 12 }}>Chain of custody</div>
            {ready ? (
              <ChainOfCustodyStitcher rows={cocRows} accent="#10B981"/>
            ) : (
              <div style={{ fontSize: 12, color: "#94A3B8", padding: "20px 0" }}>Sealing…</div>
            )}
          </motion.section>

          <motion.footer variants={item} style={{ fontSize: 11, color: "#64748B", textAlign: "center" }}>
            Verified receipts are anonymous. No rider name, address, or payment appears here.
          </motion.footer>
        </motion.div>
      </div>
    </div>
  );
}
